import { Globals } from '@/config/constants';
import { Role } from '@/models/role';
import { User } from '@/models/user';
import axios from 'axios';
import firebase from 'firebase';
import { CacheService } from '../data/cache/cacheService';
import { FirestoreService } from '../data/firebase/db/firestoreService';
import { IFirestoreService } from '../data/firebase/db/iFirestoreService';
import { ServiceContainer } from '../serviceContainer';

type Claims = { [key: string]: unknown };

export class UserService {
  private idToken: firebase.auth.IdTokenResult | undefined;

  private readonly firestoreService: IFirestoreService;
  private readonly cacheService: CacheService;

  constructor(firestoreService = ServiceContainer.get(FirestoreService), cacheService = ServiceContainer.get(CacheService)) {
    this.firestoreService = firestoreService;
    this.cacheService = cacheService;
  }

  public getCurrentUser(): firebase.User {
    return firebase.auth().currentUser!;
  }

  public async getIdToken(): Promise<firebase.auth.IdTokenResult> {
    if (!this.idToken) {
      this.idToken = await this.getCurrentUser().getIdTokenResult();
    }
    return this.idToken;
  }

  public async getClaims(): Promise<Claims> {
    return (await this.getIdToken()).claims;
  }

  public async getRoleId(): Promise<string> {
    return (await this.getClaims()).role as string;
  }

  public async getUserRole(): Promise<Role> {
    const roleId = await this.getRoleId();
    return this.firestoreService.getDocument<Role>('roles', roleId);
  }

  public async getRoleById(id: string): Promise<Role> {
    return this.firestoreService.getDocument<Role>('roles', id);
  }

  public async getAllRoles(): Promise<Role[]> {
    return this.firestoreService.getCollection<Role>('roles');
  }

  public async getAllUsers<T>(): Promise<T[]> {
    const cachedUsers = await this.cacheService.get<T[]>('users');
    if (cachedUsers) return cachedUsers;

    const token = await this.getIdToken().then((idToken) => idToken.token);

    const response = await axios.post(`${Globals.fireBaseUrl}listAllUsers`, { token: token });

    const users = response.data.sort((a: User, b: User) => {
      const nameA = a.displayName ? a.displayName.toLowerCase() : a.displayName;
      const nameB = b.displayName ? b.displayName.toLowerCase() : b.displayName;
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });

    this.cacheService.set('users', users, 60);

    return users;
  }

  public subscribeToRoles(onUpdate: (roles: Role[]) => void): () => void {
    return this.firestoreService.subscribeToCollection('roles', onUpdate);
  }

  public deleteRole(roleId: string): Promise<void> {
    return this.firestoreService.deleteDocument('roles', roleId);
  }
}
